// ──────────────────────────────────────────────
// DeviceBridgeService — Bridges mic track changes between
// RecordingService and DailyCallService.
//
// Clones the source audio track and pushes it to Daily,
// ensuring the patient always hears the correct mic while
// the recording pipeline independently captures the original.
//
// Phase 2 of Daily.co telehealth integration.
// ──────────────────────────────────────────────

import SentryService from '@services/SentryService';

import type { DailyCallService } from './DailyCallService';

const SENTRY_CATEGORY = 'devicebridge.service';

export class DeviceBridgeService {
  private dailyCallService: DailyCallService;
  private currentDailyClone: MediaStreamTrack | null = null;
  private switchQueue: Promise<void> = Promise.resolve();

  constructor(dailyCallService: DailyCallService) {
    this.dailyCallService = dailyCallService;
    dailyCallService.setCustomTrackProvider(() => this.getCurrentDailyClone());
  }

  /**
   * Called once during session setup. Clones sourceTrack, stores reference,
   * returns the clone for the caller to pass to DailyCallService.initialize({ audioSource }).
   */
  createInitialClone(sourceTrack: MediaStreamTrack): MediaStreamTrack {
    const clone = sourceTrack.clone();
    this.currentDailyClone = clone;

    SentryService.addBreadcrumb({
      category: SENTRY_CATEGORY,
      message: 'Initial clone created',
      level: 'info',
      data: {
        sourceTrackId: sourceTrack.id,
        cloneTrackId: clone.id,
      },
    });

    return clone;
  }

  /**
   * Called on device switch. Serialized via switchQueue so concurrent
   * switches execute in order — only the final device ends up active.
   */
  async handleDeviceSwitch(newSourceTrack: MediaStreamTrack): Promise<void> {
    this.switchQueue = this.switchQueue.then(() => this.doSwitch(newSourceTrack));
    return this.switchQueue;
  }

  /** Exposes the current clone — used by DailyCallService's unmute guard. */
  getCurrentDailyClone(): MediaStreamTrack | null {
    return this.currentDailyClone;
  }

  /** Stop current clone, null references, clear provider. */
  dispose(): void {
    if (this.currentDailyClone) {
      this.currentDailyClone.stop();

      SentryService.addBreadcrumb({
        category: SENTRY_CATEGORY,
        message: 'Clone stopped on dispose',
        level: 'info',
        data: { cloneTrackId: this.currentDailyClone.id },
      });
    }

    this.currentDailyClone = null;
    this.dailyCallService.setCustomTrackProvider(null);
  }

  // ── Private ──

  private async doSwitch(newSourceTrack: MediaStreamTrack): Promise<void> {
    let newClone: MediaStreamTrack;

    try {
      newClone = newSourceTrack.clone();
    } catch (err) {
      SentryService.captureException(err instanceof Error ? err : new Error(String(err)), {
        tags: { source: 'devicebridge_clone_failed' },
        extra: { sourceTrackId: newSourceTrack.id },
      });
      return;
    }

    const oldClone = this.currentDailyClone;
    if (oldClone) {
      oldClone.stop();
    }

    this.currentDailyClone = newClone;

    SentryService.addBreadcrumb({
      category: SENTRY_CATEGORY,
      message: 'Device switch — clone replaced',
      level: 'info',
      data: {
        oldCloneId: oldClone?.id ?? null,
        newCloneId: newClone.id,
      },
    });

    try {
      await this.dailyCallService.setAudioDevice(newClone);
    } catch (err) {
      SentryService.captureException(err instanceof Error ? err : new Error(String(err)), {
        tags: { source: 'devicebridge_set_audio_failed' },
        extra: { cloneTrackId: newClone.id },
      });
      // Do NOT rethrow — recording pipeline is unaffected
    }
  }
}
